import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";

export default function Settings() {
  const [totalCapacity, setTotalCapacity] = useState("");
  const [savedTotalCapacity, setSavedTotalCapacity] = useState(null);
  const [recoveryDays, setRecoveryDays] = useState(30);
  const [savedRecoveryDays, setSavedRecoveryDays] = useState(30);
  const [recoveryLimits, setRecoveryLimits] = useState({ minDays: 1, maxDays: 365 });
  const [defaultMailMessage, setDefaultMailMessage] = useState("");
  const [savedDefaultMailMessage, setSavedDefaultMailMessage] = useState("");
  const [mailMessageMaxLength, setMailMessageMaxLength] = useState(5000);
  const [pendingSection, setPendingSection] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmationError, setConfirmationError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);

  const loadSettings = useCallback(async () => {
    setError("");
    setIsLoading(true);

    try {
      const [capacity, recovery, mailNotification] = await Promise.all([
        apiFetch("/settings/mailbox-capacity"),
        apiFetch("/settings/deletion-recovery"),
        apiFetch("/settings/mail-notification"),
      ]);
      setTotalCapacity(capacity.totalCapacity ?? "");
      setSavedTotalCapacity(capacity.totalCapacity ?? null);
      setRecoveryDays(recovery.recoveryDays);
      setSavedRecoveryDays(recovery.recoveryDays);
      setRecoveryLimits({ minDays: recovery.minDays, maxDays: recovery.maxDays });
      setDefaultMailMessage(mailNotification.defaultMessage ?? "");
      setSavedDefaultMailMessage(mailNotification.defaultMessage ?? "");
      setMailMessageMaxLength(mailNotification.maxLength ?? 5000);
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Settings", action: "loadSettings" });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const capacityHasChanged = totalCapacity !== "" && Number(totalCapacity) !== savedTotalCapacity;
  const recoveryHasChanged = recoveryDays !== "" && Number(recoveryDays) !== savedRecoveryDays;
  const mailMessageHasChanged = defaultMailMessage.trim() !== ""
    && defaultMailMessage !== savedDefaultMailMessage;

  const requestConfirmation = (event, section) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setPassword("");
    setConfirmationError("");
    setPendingSection(section);
  };

  const closeConfirmation = () => {
    if (savingSection) return;
    setPendingSection(null);
    setPassword("");
    setConfirmationError("");
  };

  const savePendingChange = async (event) => {
    event.preventDefault();
    if (!pendingSection) return;

    const section = pendingSection;
    const isCapacity = section === "capacity";
    const isRecovery = section === "recovery";
    const endpoint = isCapacity
      ? "/settings/mailbox-capacity"
      : isRecovery
        ? "/settings/deletion-recovery"
        : "/settings/mail-notification";
    const body = isCapacity
      ? { totalCapacity: Number(totalCapacity), password }
      : isRecovery
        ? { recoveryDays: Number(recoveryDays), password }
        : { defaultMessage: defaultMailMessage, password };
    setConfirmationError("");
    setSavingSection(section);

    try {
      const data = await apiFetch(endpoint, {
        method: "PATCH",
        body: JSON.stringify(body),
      });

      if (isCapacity) {
        setTotalCapacity(data.totalCapacity);
        setSavedTotalCapacity(data.totalCapacity);
        setSuccess("Mailbox capacity updated.");
      } else if (isRecovery) {
        setRecoveryDays(data.recoveryDays);
        setSavedRecoveryDays(data.recoveryDays);
        setSuccess("Deleted item recovery period updated. Existing deadlines were not changed.");
      } else {
        setDefaultMailMessage(data.defaultMessage);
        setSavedDefaultMailMessage(data.defaultMessage);
        setSuccess("Default mail notification message updated.");
      }
      setPendingSection(null);
      setPassword("");
    } catch (err) {
      setConfirmationError(err.message);
      logClientError(err, {
        page: "Settings",
        action: isCapacity
          ? "updateMailboxCapacity"
          : isRecovery
            ? "updateDeletionRecovery"
            : "updateMailNotification",
      });
    } finally {
      setSavingSection(null);
    }
  };

  const confirmationDetails = {
    capacity: {
      title: "Save mailbox capacity?",
      description: "Enter your current password to save this settings change.",
      summary: `Total mailbox capacity: ${savedTotalCapacity ?? "Not set"} → ${totalCapacity}`,
      confirmLabel: "Confirm and Save Capacity",
    },
    recovery: {
      title: "Save recovery period?",
      description: "Enter your current password to save this settings change.",
      summary: `Recovery period: ${savedRecoveryDays} days → ${recoveryDays} days`,
      confirmLabel: "Confirm and Save Recovery Period",
    },
    mailNotification: {
      title: "Save default email message?",
      description: "Enter your current password to save this settings change.",
      summary: "The new template will be used to prepare future mailbox notification emails.",
      confirmLabel: "Confirm and Save Message",
    },
  }[pendingSection];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Configuration</p>
          <h2>Settings</h2>
        </div>
      </div>

      {error && <p className="admin-empty-state admin-error-state">{error}</p>}
      {success && <p className="admin-empty-state admin-success-state">{success}</p>}

      {isLoading ? (
        <p className="admin-empty-state">Loading settings...</p>
      ) : (
        <div className="admin-settings-grid">
          <section className="admin-form-panel" aria-labelledby="capacity-setting-title">
            <div className="admin-setting-intro">
              <h3 id="capacity-setting-title">Mailbox capacity</h3>
              <p>Controls capacity reporting on the dashboard.</p>
            </div>
            <form className="admin-form" onSubmit={(event) => requestConfirmation(event, "capacity")}>
              <label>
                Total Mailbox Capacity
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={totalCapacity}
                  onChange={(event) => setTotalCapacity(event.target.value)}
                  required
                />
              </label>
              <div className="admin-form-actions">
                <button
                  className="admin-button admin-button-primary"
                  type="submit"
                  disabled={savingSection !== null || !capacityHasChanged}
                >
                  Save Capacity
                </button>
              </div>
            </form>
          </section>

          <section className="admin-form-panel" aria-labelledby="recovery-setting-title">
            <div className="admin-setting-intro">
              <h3 id="recovery-setting-title">Deleted item recovery</h3>
              <p>Applies to future tenant and document deletions. AWS retains purged object versions separately.</p>
            </div>
            <form className="admin-form" onSubmit={(event) => requestConfirmation(event, "recovery")}>
              <label>
                Recovery Period (days)
                <input
                  type="number"
                  min={recoveryLimits.minDays}
                  max={recoveryLimits.maxDays}
                  step="1"
                  value={recoveryDays}
                  onChange={(event) => setRecoveryDays(event.target.value)}
                  required
                />
              </label>
              <small className="admin-field-help">
                Choose {recoveryLimits.minDays}–{recoveryLimits.maxDays} days. Expired items require manual purge from Trash.
              </small>
              <div className="admin-form-actions">
                <button
                  className="admin-button admin-button-primary"
                  type="submit"
                  disabled={savingSection !== null || !recoveryHasChanged}
                >
                  Save Recovery Period
                </button>
              </div>
            </form>
          </section>

          <section className="admin-form-panel" aria-labelledby="mail-notification-setting-title">
            <div className="admin-setting-intro">
              <h3 id="mail-notification-setting-title">Default email message</h3>
              <p>Used as the starting message when an admin sends a mailbox notification.</p>
            </div>
            <form className="admin-form" onSubmit={(event) => requestConfirmation(event, "mailNotification")}>
              <label className="admin-field-wide">
                Message Template
                <textarea
                  value={defaultMailMessage}
                  onChange={(event) => setDefaultMailMessage(event.target.value)}
                  maxLength={mailMessageMaxLength}
                  required
                />
              </label>
              <small className="admin-field-help admin-field-wide">
                Use {"{tenantName}"} for the tenant’s name and {"{mailboxNumber}"} for the mailbox number. You can review and edit the rendered message before sending.
              </small>
              <div className="admin-form-actions">
                <button
                  className="admin-button admin-button-primary"
                  type="submit"
                  disabled={savingSection !== null || !mailMessageHasChanged}
                >
                  Save Default Message
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {pendingSection && (
        <PasswordConfirmationModal
          {...confirmationDetails}
          password={password}
          error={confirmationError}
          isSubmitting={savingSection !== null}
          onPasswordChange={setPassword}
          onConfirm={savePendingChange}
          onCancel={closeConfirmation}
        />
      )}
    </div>
  );
}

function PasswordConfirmationModal({
  title,
  description,
  summary,
  confirmLabel,
  password,
  error,
  isSubmitting,
  onPasswordChange,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSubmitting, onCancel]);

  return (
    <div
      className="admin-modal-backdrop admin-confirm-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onCancel();
      }}
    >
      <section className="admin-modal admin-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="settings-confirm-title">
        <div className="admin-modal-header">
          <div>
            <p className="admin-page-eyebrow">Authentication required</p>
            <h2 id="settings-confirm-title">{title}</h2>
            <p>{description}</p>
          </div>
        </div>
        <form onSubmit={onConfirm}>
          <div className="admin-modal-body admin-settings-confirm-body">
            <p className="admin-page-note">{summary}</p>
            <label>
              Current Password
              <input
                type="password"
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </label>
            {error && <p className="admin-form-error" role="alert">{error}</p>}
          </div>
          <div className="admin-modal-footer">
            <div className="admin-form-actions">
              <button className="admin-button admin-button-secondary" type="button" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </button>
              <button className="admin-button admin-button-primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : confirmLabel}
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}
